/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { DatePicker as AntDatePicker, TimePicker as AntTimePicker } from 'antd'
import { Dayjs } from 'dayjs'
import { paragraphSmall, weightRegular } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import {
  withDefaultTime,
  withOrderedWindow,
  type RegistrationWindowChange,
} from './registrationWindow'

export type { RegistrationWindowChange }

type RegistrationWindowFieldsProps = {
  /** Heading for the whole window, carrying the timezone note. */
  label: string
  startsAt: Dayjs | null
  endsAt: Dayjs | null
  fieldErrors: string[]
  customError: Record<string, string> | undefined
  onChange: (next: RegistrationWindowChange) => void
}

const FIELD_NAME = 'competitionDateRange'

// `type: 'mask'` gives a guided input — the placeholder stays in place and the
// caret steps through the segments as the admin types, so a partially entered
// value is not rejected the way a plain format string would reject it.
const DATE_MASK = { format: 'MM/DD/YYYY', type: 'mask' } as const

// Deliberately NOT masked. rc-picker's MaskFormat only treats
// ['YYYY','MM','DD','HH','mm','ss','SSS'] as editable cells, so under a mask a
// 12-hour `hh:mm A` exposes only the minutes — the hours and the AM/PM marker
// are not cells and cannot be typed. A plain format keeps the field free-text:
// the admin types the whole time and it is parsed on blur/enter.
const TIME_FORMAT = 'hh:mm A'

const rowStyle = css`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
  min-width: 0;
`

// Matches the shared DatePicker/RangePicker chrome so the split fields sit in
// the form exactly like the range picker they replace.
const controlStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  height: 40px !important;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
`

const iconStyle = css`
  color: ${colors.WHITE};
  font-size: 18px;
`

const TIME_POPUP_CLASS = 'registration-window-time-popup'

// antd renders the time columns in a portal, so the selected cell is styled
// through a popup class rather than the trigger's css prop.
//
// DEFAULT_FLAIR is the same token the theme feeds antd as `colorPrimary`
// (web/src/configs), so the selected time cell matches the selected day in the
// calendar rather than approximating it. White text is used on both tenants.
const timePopupStyles = css`
  .${TIME_POPUP_CLASS}
    .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: ${colors.DEFAULT_FLAIR};
    color: ${colors.WHITE};

    &:hover {
      background: ${colors.DEFAULT_FLAIR};
      color: ${colors.WHITE};
    }
  }
`

/**
 * Registration window as four independent fields — start date, start time,
 * end date, end time.
 *
 * A single antd RangePicker commits one panel per OK and emits the untouched
 * end as `undefined`, which silently discarded whichever date the admin had
 * already chosen. Separate fields sidestep that entirely: each control owns one
 * value and commits on its own.
 *
 * No end is blocked from being picked: choosing an out-of-order date is how the
 * admin restarts the window, so `withOrderedWindow` clears the now-stale other
 * end rather than refusing the click.
 */
const RegistrationWindowFields = ({
  label,
  startsAt,
  endsAt,
  fieldErrors,
  customError,
  onChange,
}: RegistrationWindowFieldsProps) => {
  const hasError =
    fieldErrors?.includes(FIELD_NAME) || Boolean(customError?.[FIELD_NAME])
  const status = hasError ? 'error' : ''

  const popupContainer = (triggerNode: HTMLElement) =>
    triggerNode.parentNode as HTMLElement

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-self: stretch;
        width: 100%;
      `}
    >
      <Global styles={timePopupStyles} />
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {label}
      </Text>

      <div css={rowStyle}>
        <div css={fieldStyle}>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Start Date
          </Text>
          <AntDatePicker
            value={startsAt}
            onChange={(value) =>
              onChange(
                withOrderedWindow(
                  {
                    startsAt: withDefaultTime(value, startsAt, 'start'),
                    endsAt,
                  },
                  'start'
                )
              )
            }
            format={DATE_MASK}
            status={status}
            placeholder='Start date'
            css={controlStyle}
            suffixIcon={<CalendarOutlined css={iconStyle} />}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={fieldStyle}>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Start Time
          </Text>
          <AntTimePicker
            value={startsAt}
            onChange={(value) =>
              onChange(withOrderedWindow({ startsAt: value, endsAt }, 'start'))
            }
            format={TIME_FORMAT}
            use12Hours
            status={status}
            // A time cannot be stored without a day, so stay disabled until the
            // date is chosen rather than silently attaching it to today.
            disabled={!startsAt}
            needConfirm={false}
            placeholder='Start time'
            css={controlStyle}
            suffixIcon={<ClockCircleOutlined css={iconStyle} />}
            popupClassName={TIME_POPUP_CLASS}
            getPopupContainer={popupContainer}
          />
        </div>
      </div>

      <div css={rowStyle}>
        <div css={fieldStyle}>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            End Date
          </Text>
          <AntDatePicker
            value={endsAt}
            onChange={(value) =>
              onChange(
                withOrderedWindow(
                  {
                    startsAt,
                    endsAt: withDefaultTime(value, endsAt, 'end'),
                  },
                  'end'
                )
              )
            }
            format={DATE_MASK}
            status={status}
            placeholder='End date'
            css={controlStyle}
            suffixIcon={<CalendarOutlined css={iconStyle} />}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={fieldStyle}>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            End Time
          </Text>
          <AntTimePicker
            value={endsAt}
            onChange={(value) =>
              onChange(withOrderedWindow({ startsAt, endsAt: value }, 'end'))
            }
            format={TIME_FORMAT}
            use12Hours
            status={status}
            disabled={!endsAt}
            needConfirm={false}
            placeholder='End time'
            css={controlStyle}
            suffixIcon={<ClockCircleOutlined css={iconStyle} />}
            popupClassName={TIME_POPUP_CLASS}
            getPopupContainer={popupContainer}
          />
        </div>
      </div>

      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={FIELD_NAME}
      />
    </div>
  )
}

export default RegistrationWindowFields
